import Decimal from 'decimal.js';
/**
 * 정확한 소수점 계산을 위한 Decimal 유틸리티 함수들
 * 부동소수점 오차 없이 금융 계산을 수행합니다.
 */
// Decimal 클래스와 상수를 re-export
export { Decimal };
// 반올림 모드 상수 export
export const ROUND_HALF_UP = Decimal.ROUND_HALF_UP;
export const ROUND_DOWN = Decimal.ROUND_DOWN;
export const ROUND_UP = Decimal.ROUND_UP;
/**
 * 정확한 덧셈 계산
 * @param a - 첫 번째 숫자
 * @param b - 두 번째 숫자
 * @returns 계산 결과 (number)
 * @example
 * add(0.1, 0.2) // 0.3 (정확함, 일반 0.1 + 0.2는 0.30000000000000004)
 */
export function add(a, b) {
    return new Decimal(a).plus(b).toNumber();
}
/**
 * 정확한 뺄셈 계산
 * @param a - 첫 번째 숫자
 * @param b - 두 번째 숫자
 * @returns 계산 결과 (number)
 * @example
 * subtract(1.14868, 0.00002) // 1.14866 (정확함)
 */
export function subtract(a, b) {
    return new Decimal(a).minus(b).toNumber();
}
/**
 * 정확한 곱셈 계산
 * @param a - 첫 번째 숫자
 * @param b - 두 번째 숫자
 * @returns 계산 결과 (number)
 * @example
 * multiply(0.2, 0.1) // 0.02 (정확함, 일반 0.2 * 0.1은 0.020000000000000004)
 */
export function multiply(a, b) {
    return new Decimal(a).times(b).toNumber();
}
/**
 * 정확한 나눗셈 계산
 * @param a - 피제수
 * @param b - 제수
 * @returns 계산 결과 (number)
 * @example
 * divide(1, 3) // 0.3333333333333333 (정확함)
 */
export function divide(a, b) {
    return new Decimal(a).dividedBy(b).toNumber();
}
/**
 * 여러 숫자의 합계 계산
 * @param values - 계산할 숫자 배열
 * @returns 합계 (number)
 * @example
 * sum([0.1, 0.2, 0.3]) // 0.6 (정확함)
 */
export function sum(...values) {
    return values.reduce((acc, val) => new Decimal(acc).plus(val), new Decimal(0)).toNumber();
}
/**
 * 여러 숫자의 곱 계산
 * @param values - 계산할 숫자 배열
 * @returns 곱 (number)
 * @example
 * product([0.5, 0.2, 0.1]) // 0.01 (정확함)
 */
export function product(...values) {
    return values.reduce((acc, val) => new Decimal(acc).times(val), new Decimal(1)).toNumber();
}
/**
 * 소수점 자리수에 맞춰 반올림
 * @param value - 반올림할 숫자
 * @param decimalPlaces - 소수점 자리수
 * @returns 반올림된 숫자 (number)
 * @example
 * round(1.1486999999999998, 5) // 1.14870
 */
export function round(value, decimalPlaces) {
    return new Decimal(value).toDecimalPlaces(decimalPlaces).toNumber();
}
/**
 * Decimal 인스턴스를 직접 사용할 수 있는 헬퍼
 * 복잡한 계산이 필요한 경우 사용
 * @param value - 초기값
 * @returns Decimal 인스턴스
 * @example
 * const result = decimal(1.14868)
 *   .plus(0.2)
 *   .times(0.0001)
 *   .toNumber()
 */
export function decimal(value) {
    return new Decimal(value);
}
/**
 * 숫자를 정확한 문자열로 변환
 * 부동소수점 오차 없이 문자열로 변환합니다.
 * @param value - 변환할 숫자
 * @param decimalPlaces - 소수점 자리수 (선택사항, 지정하지 않으면 자동)
 * @returns 정확한 문자열 표현
 * @example
 * toDecimalString(0.1 + 0.2) // "0.3" (정확함)
 * toDecimalString(1.14868, 5) // "1.14868"
 */
export function toDecimalString(value, decimalPlaces) {
    const decimal = new Decimal(value);
    if (decimalPlaces !== undefined) {
        return decimal.toFixed(decimalPlaces);
    }
    return decimal.toString();
}
/**
 * 퍼센트 기반 금액 계산
 * 오차 없이 계산한 후 소수점 2자리에서 반올림
 * @param amount - 기준 금액
 * @param percentage - 퍼센트 값 (예: 25, 50, 75, 100)
 * @returns 계산된 금액 (소수점 2자리까지)
 * @example
 * calculatePercentageAmount(1000, 25) // 250.00
 * calculatePercentageAmount(1234.567, 50) // 617.28
 */
export function calculatePercentageAmount(amount, percentage) {
    return new Decimal(amount)
        .mul(percentage)
        .div(100)
        .toDecimalPlaces(3, Decimal.ROUND_HALF_UP)
        .toNumber();
}
//# sourceMappingURL=decimal.js.map